import { useRef, type CSSProperties } from 'react';

import { groupDigits, ungroupDigits } from '@/lib/money';

/**
 * 금액 입력칸 — <b>치는 동안 쉼표가 붙는다</b> (사용자 지시 2026-09-14 · 2026-09-15 「모든 금액 칸」).
 *
 * <h2>왜 {@code type="number"} 가 아닌가</h2>
 * 그쪽은 쉼표가 든 값을 «잘못된 값» 으로 보고 <b>칸을 통째로 비운다</b>. `1,078,000,000` 을 넣으면
 * 아무것도 남지 않는다. 그래서 글자칸으로 두고 {@code inputMode="decimal"} 로 <b>숫자 자판</b>만
 * 불러온다 — 현장에서 패드로 치는 화면이 있어 이것이 실제 차이를 만든다.
 *
 * <p>같은 이유로 {@code min} 을 못 쓴다. 음수를 막아야 하는 자리는 <b>부르는 쪽이 저장할 때</b>
 * 본다 — 치는 도중에 막으면 「-」 한 글자를 지우지도 못한다.
 *
 * <h2>커서가 튀지 않게</h2>
 * 쉼표를 다시 그리면 커서는 기본적으로 <b>맨 뒤로 간다</b>. 열 자리 금액의 앞쪽 한 자리를 고치려던
 * 사람은 매번 뒤로 밀려나고, 그러면 결국 다 지우고 새로 친다. 그래서 <b>커서 앞의 숫자 개수</b>를
 * 세어 두고, 다시 그린 글자에서 같은 개수를 지난 자리에 되돌려 놓는다 — 쉼표가 몇 개 늘든 줄든
 * «몇 번째 숫자» 는 변하지 않는다.
 *
 * <p>부르는 쪽이 드는 값은 <b>쉼표 없는 글자</b>다. 저장할 때 {@code Number()} 가 그대로 읽는다.
 */
export function AmountInput({
  value,
  onChange,
  className = 'input-field',
  style,
  disabled,
  placeholder,
  ariaLabel,
  onKeyDown,
}: {
  /** 쉼표 없는 숫자 글자. 빈 문자열은 「안 적음」이다 */
  value: string;
  onChange: (next: string) => void;
  /** 화면마다 입력칸 클래스가 다르다(`input-field` · `input`) — 부르는 쪽이 정한다 */
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);

  const handle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const caret = event.target.selectionStart ?? raw.length;
    const digitsBefore = ungroupDigits(raw.slice(0, caret)).replace(/[.-]/g, '').length;
    onChange(ungroupDigits(raw));

    // 다시 그려진 <b>뒤</b>에 커서를 옮긴다. 지금 옮기면 React 가 값을 써 넣으며 덮는다.
    requestAnimationFrame(() => {
      const input = ref.current;
      if (!input) return;
      const shown = input.value;
      let at = 0;
      let seen = 0;
      while (at < shown.length && seen < digitsBefore) {
        if (/\d/.test(shown[at]!)) seen += 1;
        at += 1;
      }
      input.setSelectionRange(at, at);
    });
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      className={className}
      style={{ textAlign: 'right', ...style }}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel}
      value={groupDigits(value)}
      onChange={handle}
      onKeyDown={onKeyDown}
      // 표 안에 서는 경우가 있다 — 줄 클릭이 창을 여는 표에서 칸을 누르면 창이 뜨면 안 된다.
      onClick={(event) => event.stopPropagation()}
    />
  );
}
